import { useNavigate, createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Users,
  Download,
  Send,
  Search,
  Clock,
  UserCheck,
  Hash,
  AlertCircle,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";

// --- Types ---

interface WaitlistEntry {
  id: string;
  email: string;
  name: string | null;
  referralCode: string;
  referralCount: number;
  referredById: string | null;
  position: number;
  effectivePosition: number;
  invitedAt: string | null;
  createdAt: string;
}

interface TopReferrer {
  email: string;
  referralCount: number;
  effectivePosition: number;
}

interface WaitlistStats {
  total: number;
  invited: number;
  pending: number;
  avgReferrals: number;
  topReferrers?: TopReferrer[];
}

// --- Helpers ---

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

function relativeDate(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSeconds < 60) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks === 1) return "1 week ago";
  if (diffWeeks < 5) return `${diffWeeks} weeks ago`;
  if (diffMonths === 1) return "1 month ago";
  return `${diffMonths} months ago`;
}

// --- Main Page ---

function WaitlistPage() {
  const navigate = useNavigate();
    // Data
  const [entries, setEntries] = React.useState<WaitlistEntry[]>([]);
  const [stats, setStats] = React.useState<WaitlistStats | null>(null);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Filters
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState(
    "all"
  );
  const [sortBy, setSortBy] = React.useState(
    "position"
  );

  // Pagination
  const [page, setPage] = React.useState(
    parseInt("1")
  );
  const limit = 25;

  // Selection
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = React.useState(false);
  const [inviting, setInviting] = React.useState(false);

  // --- URL Sync ---

  const updateURL = React.useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (sortBy !== "position") params.set("sort", sortBy);
    if (page !== 1) params.set("page", page.toString());

    const queryString = params.toString();
    navigate({ to: queryString ? `?${queryString}` : "/dashboard/waitlist" });
  }, [search, statusFilter, sortBy, page, navigate]);

  // --- Data Fetching ---

  const fetchEntries = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("page", page.toString());
      if (search) params.set("search", search);
      if (statusFilter !== "all") params.set("filter", statusFilter);
      if (sortBy) params.set("sort", sortBy);

      const response = await fetch(
        `${API_ADMIN_BASE_URL}/api/admin/waitlist?${params}`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Failed to fetch waitlist entries");

      const data = await response.json();
      if (!data.success || !data.data) {
        throw new Error(data.error || "Invalid response");
      }
      setEntries(data.data.entries || []);
      setTotal(data.data.total || 0);
      if (data.data.stats) {
        setStats(data.data.stats);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      console.error("Error fetching waitlist:", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, sortBy, page, limit]);

  const fetchStats = React.useCallback(async () => {
    try {
      const response = await fetch(
        `${API_ADMIN_BASE_URL}/api/admin/waitlist/stats`,
        { credentials: "include" }
      );
      if (!response.ok) return;
      const data = await response.json();
      if (data.success && data.data) {
        // Merge with existing stats to preserve avgReferrals from list endpoint
        setStats((prev) => prev ? { ...prev, ...data.data } : data.data);
      }
    } catch (err) {
      console.error("Error fetching waitlist stats:", err);
    }
  }, []);

  // --- Actions ---

  const handleSendInvites = async () => {
    setInviting(true);
    try {
      const response = await fetch(
        `${API_ADMIN_BASE_URL}/api/admin/waitlist/invite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ids: Array.from(selectedIds) }),
        }
      );
      if (!response.ok) throw new Error("Failed to send invites");

      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Invite failed");
      toast.success(
        `Successfully invited ${data.data?.invited || selectedIds.size} user(s)`
      );
      setSelectedIds(new Set());
      setShowInviteDialog(false);
      fetchEntries();
      fetchStats();
    } catch (err) {
      console.error("Error sending invites:", err);
      toast.error("Failed to send invites");
    } finally {
      setInviting(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await fetch(
        `${API_ADMIN_BASE_URL}/api/admin/waitlist/export`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Failed to export");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `waitlist-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Waitlist exported");
    } catch (err) {
      console.error("Error exporting CSV:", err);
      toast.error("Failed to export waitlist");
    }
  };

  // --- Selection ---

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === entries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(entries.map((e) => e.id)));
    }
  };

  const pendingSelectedCount = entries.filter(
    (e) => selectedIds.has(e.id) && !e.invitedAt
  ).length;

  // --- Effects ---

  React.useEffect(() => {
    updateURL();
  }, [updateURL]);
  React.useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);
  React.useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  React.useEffect(() => {
    document.title = "Waitlist - Admin";
  }, []);
  React.useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [search, statusFilter, sortBy]);

  const totalPages = Math.ceil(total / limit);

  // --- Render ---

  return (
    <div className="bg-background h-full overflow-auto">
      {/* Header */}
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Waitlist</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="border-border flex flex-col border-b px-4 py-4 md:px-6 md:py-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                Waitlist Management
              </h1>
              <p className="text-muted-foreground text-sm">
                {formatNumber(total)} entries
                {stats && (
                  <span className="ml-2">
                    · {formatNumber(stats.pending)} pending ·{" "}
                    {formatNumber(stats.invited)} invited
                  </span>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Export CSV
              </Button>
              <Button
                size="sm"
                onClick={() => setShowInviteDialog(true)}
                disabled={pendingSelectedCount === 0}
              >
                <Send className="mr-1.5 h-3.5 w-3.5" />
                Send Invites
                {pendingSelectedCount > 0 && (
                  <span className="ml-1.5 rounded-full bg-primary-foreground/20 px-1.5 py-0.5 text-xs">
                    {pendingSelectedCount}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats ? (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Total</p>
                  <p className="text-lg font-semibold">
                    {formatNumber(stats.total)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-green-500/10">
                  <UserCheck className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Invited</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatNumber(stats.invited)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-500/10">
                  <Clock className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Pending</p>
                  <p className="text-lg font-semibold text-amber-600">
                    {formatNumber(stats.pending)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Avg Referrals</p>
                  <p className="text-lg font-semibold">
                    {stats.avgReferrals.toFixed(1)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-[72px] rounded-lg" />
              ))}
            </div>
          )}

          {/* Top Referrers Leaderboard */}
          {stats?.topReferrers && stats.topReferrers.length > 0 && (
            <div className="mt-4 rounded-lg border p-4">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-4 w-4 text-amber-500" />
                <h3 className="text-sm font-semibold">Top Referrers</h3>
              </div>
              <div className="grid gap-2">
                {stats.topReferrers.slice(0, 10).map((referrer, i) => (
                  <div
                    key={referrer.email}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={cn(
                        "w-5 text-center font-mono text-xs",
                        i === 0 ? "text-amber-500 font-bold" :
                        i === 1 ? "text-zinc-400 font-bold" :
                        i === 2 ? "text-amber-700 font-bold" :
                        "text-muted-foreground"
                      )}>
                        {i + 1}
                      </span>
                      <span className="truncate">{referrer.email}</span>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <span className="text-xs text-muted-foreground">
                        #{formatNumber(referrer.effectivePosition)}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {referrer.referralCount} referrals
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        {/* Controls Bar */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full md:w-[300px]">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Search by email..."
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex gap-3 flex-wrap items-center">
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="invited">Invited</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="position">Position</SelectItem>
                <SelectItem value="createdAt">Date Joined</SelectItem>
                <SelectItem value="referralCount">Referrals</SelectItem>
              </SelectContent>
            </Select>

            {selectedIds.size > 0 && (
              <span className="text-muted-foreground text-sm">
                {selectedIds.size} selected
              </span>
            )}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="flex items-center gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-sm font-medium">Failed to load waitlist</p>
              <p className="text-muted-foreground text-xs">{error}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={fetchEntries}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={
                        entries.length > 0 &&
                        selectedIds.size === entries.length
                      }
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead className="w-[70px]">Position</TableHead>
                  <TableHead className="w-[220px]">Email</TableHead>
                  <TableHead className="w-[150px]">Name</TableHead>
                  <TableHead className="w-[120px]">Referral Code</TableHead>
                  <TableHead className="w-[80px]">Referrals</TableHead>
                  <TableHead className="w-[180px]">Referred By</TableHead>
                  <TableHead className="w-[90px]">Status</TableHead>
                  <TableHead className="w-[110px]">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-4" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-8" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-8" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-12 text-muted-foreground"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Users className="h-8 w-8 opacity-40" />
                        <p className="text-sm font-medium">
                          No waitlist entries found
                        </p>
                        <p className="text-xs">
                          {search || statusFilter !== "all"
                            ? "Try adjusting your filters"
                            : "Entries will appear here when users join the waitlist"}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow
                      key={entry.id}
                      className={cn(
                        selectedIds.has(entry.id) && "bg-muted/50"
                      )}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(entry.id)}
                          onCheckedChange={() => toggleSelect(entry.id)}
                          aria-label={`Select ${entry.email}`}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono text-muted-foreground" title={`Original: #${entry.position}`}>
                          #{entry.effectivePosition}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm truncate block max-w-[210px]">
                          {entry.email}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm truncate block max-w-[140px]">
                          {entry.name || (
                            <span className="text-muted-foreground">--</span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono text-muted-foreground">
                          {entry.referralCode}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "text-sm font-medium",
                            entry.referralCount > 0
                              ? "text-foreground"
                              : "text-muted-foreground"
                          )}
                        >
                          {formatNumber(entry.referralCount)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm truncate block max-w-[170px] text-muted-foreground">
                          {entry.referredById ? (
                            <span className="font-mono text-xs">{entry.referredById.slice(0, 8)}...</span>
                          ) : (
                            <span className="opacity-50">&mdash;</span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            entry.invitedAt
                              ? "default"
                              : "secondary"
                          }
                          className={cn(
                            "text-xs",
                            entry.invitedAt
                              ? "bg-green-500/15 text-green-600 hover:bg-green-500/20"
                              : "bg-amber-500/15 text-amber-600 hover:bg-amber-500/20"
                          )}
                        >
                          {entry.invitedAt ? "Invited" : "Pending"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span
                          className="text-muted-foreground text-xs"
                          title={new Date(entry.createdAt).toLocaleString()}
                        >
                          {relativeDate(entry.createdAt)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage(Math.max(1, page - 1))}
                  className={
                    page === 1
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => setPage(pageNum)}
                      isActive={page === pageNum}
                      className="cursor-pointer"
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  className={
                    page === totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>

      {/* Send Invites Confirmation Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Send Invites</DialogTitle>
            <DialogDescription>
              You are about to send invite emails to{" "}
              <span className="font-semibold text-foreground">
                {pendingSelectedCount}
              </span>{" "}
              pending user{pendingSelectedCount !== 1 ? "s" : ""}. This will
              mark them as invited and send an email with access instructions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteDialog(false)}
              disabled={inviting}
            >
              Cancel
            </Button>
            <Button onClick={handleSendInvites} disabled={inviting}>
              {inviting ? "Sending..." : `Send ${pendingSelectedCount} Invite${pendingSelectedCount !== 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/waitlist')({
  component: WaitlistPage,
})
